import React, {
  ReactElement, Ref, useEffect, useRef, useState,
} from 'react';
import {
  View, StyleSheet, KeyboardAvoidingView, Text, ScrollView, TextInput, Platform, ActivityIndicator,
  FlatList, Pressable,
} from 'react-native';
import Modal from 'react-native-modal';
import { EdgeInsets, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { useNavigation } from '@react-navigation/native';
import Colors from '../../../constants/Colors';
import Button from '../../Button';
import IssueList, { customTemplateDefaultIssues } from './components/IssueList';
import AlertModal from '../../AlertModal';
import Style from '../../../constants/Style';
import FieldsList from './components/FieldList';
import CloseModalIcon from '../../CloseModalIcon';
import updateTemplate from '../../../requests/Assets/UpdateTemplate';
import isMobile from '../../IsMobile';
import nameRegex from '../../Regex';
import getUserGroups from '../../../requests/Users/GetUserGroups';
import PresetTemplates from './components/PresetTemplates';
import { TemplateUpdateOption } from './components/CreationOptionList/components/CreationOption';
import { logEvent } from '../../LogEvent';
import {
  TemplateIssue, Template, TemplateField, TemplateGroup, UserGroup, TemplateType,
} from '../../../constants/Types';
import MainSection from './components/MainSection';
import TemplateList from './components/TemplateList';
import CustomTextField from '../../CustomTextField';
import { ADDITIONAL_FIELD_TYPES } from './components/FieldList/components/FieldItem';
import TabList, { UpdateTemplateSection } from './components/TabList';
import CreationOptionList from './components/CreationOptionList';
import PressableStyled from '../../PressableStyled';
import { Building } from '../../../screens/LocationManagement/components/AddBuildingModal';
import { DEFAULT_TEMPLATE_TITLE } from '../../../constants/Utils';
import { TEMPLATE_NAME_DUPLICATE_ERROR } from '../../../constants/AlertMessages';

interface AddTemplateModalProps {
  isVisible: boolean,
  hide: () => void,
  templateGroups: TemplateGroup[],
  templateToUpdate?: Template,
  defaultTemplateGroupID?: string,
  // eslint-disable-next-line no-unused-vars
  update: (template: Template, add: boolean, groupID: string) => void,
  showParentAlert,
}

const AddTemplateModal = ({
  isVisible, hide, templateToUpdate = null, templateGroups = [], defaultTemplateGroupID, update,
  showParentAlert,
}: AddTemplateModalProps) => {
  const insets: EdgeInsets = useSafeAreaInsets();
  const scrollView: React.Ref<ScrollView> = useRef<ScrollView>();
  const navigation = useNavigation();
  const templateList: React.Ref<FlatList> = useRef<FlatList>();
  const [updatedTemplate, setUpdatedTemplate] = useState<Template>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertShown, setAlertShown] = useState<boolean>(false);
  const [alertDismissed, setAlertDismissed] = useState<boolean>(!alertShown);
  const [alertTitle, setAlertTitle] = useState<string>(null);
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>(null);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [visibilityToggleEnabled, setVisibilityToggleEnabled] = useState<boolean>(true);
  const [initialOption, setInitialOption] = useState<TemplateUpdateOption>(templateToUpdate
    ? TemplateUpdateOption.EDIT : null);
  const [primedTemplateId, setPrimedTemplateId] = useState<string>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(templateToUpdate?.id);
  const [selectedTemplateName,
    setSelectedTemplateName] = useState<string>(templateToUpdate?.name ?? 'New template');
  const [templateTitle,
    setTemplateTitle] = useState<string>(templateToUpdate?.title || DEFAULT_TEMPLATE_TITLE);
  const [templateSubtitle,
    setTemplateSubtitle] = useState<string | null>(templateToUpdate?.subtitle ?? null);
  const [templateSubtitleUrl,
    setTemplateSubtitleUrl] = useState<string | null>(templateToUpdate?.subtitleUrl ?? null);
  const [sectionShown,
    setSectionShown] = useState<UpdateTemplateSection>(UpdateTemplateSection.MAIN);
  const [fields, setFields] = useState<TemplateField[]>([]);
  const [issues, setIssues] = useState<TemplateIssue[]>(customTemplateDefaultIssues());
  const [photoUploadEnabled,
    setPhotoUploadEnabled] = useState<boolean>(!templateToUpdate?.disablePhotoUpload);
  const [templateGroupDropdownShown, setTemplateGroupDropdownShown] = useState<boolean>(false);
  const [templateTypeDropdownShown, setTemplateTypeDropdownShown] = useState<boolean>(false);
  const [focusedFieldIndex, setFocusedFieldIndex] = useState<number>(-1);
  const nameField: Ref<TextInput> = useRef<TextInput>();
  const fieldRefs = [];
  const [selectedTemplateGroupId, setSelectedTemplateGroupId] = useState<string>(
    defaultTemplateGroupID ?? templateGroups?.filter((group: TemplateGroup) => group
      .buttonPrototypes?.map((item: Template) => item.id)?.includes(templateToUpdate?.id))[0]
      ?.ButtonPrototypeGroupId ?? templateGroups[0]?.ButtonPrototypeGroupId,
  );
  const [selectedTemplateType,
    setSelectedTemplateType] = useState<TemplateType>(templateToUpdate?.type ?? TemplateType.ASSET);
  const [templateGroupNotSelected, setTemplateGroupNotSelected] = useState<boolean>(false);
  const [fieldTypesShownIndex, setFieldTypesShownIndex] = useState<number>(null);
  const [priorityDropdownShownIndex, setPriorityDropdownShownIndex] = useState<number | null>(-2);
  const [issueTagDropdownShownIndex, setIssueTagDropdownShownIndex] = useState<number | null>(-2);
  const [tagDropdownShownIndex, setTagDropdownShownIndex] = useState<number>(-1);
  const [categoryDropdownShownIndex, setCategoryDropdownShownIndex] = useState<number | null>(null);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const selectSlaNames = createSelector(
    (state: { selectedBuilding: Building }) => state.selectedBuilding.SlaHours,
    (slaHours) => slaHours?.map((item) => item.Name) ?? [],
  );
  const slaNames = useSelector(selectSlaNames);
  const happyEnabled = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);
  const [modalVisible, setModalVisible] = useState<boolean>(isVisible);
  const templates = templateGroups.reduce((prev, current: TemplateGroup) => {
    prev.push(...current.buttonPrototypes);
    return prev;
  }, []).sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));
  const [ppmLink, setPpmLink] = useState<string>(templateToUpdate?.ppmUrl ?? '');
  const [ppmLinkError, setPpmLinkError] = useState<string | null>(null);
  const [subtitleUrlInvalid, setSubtitleUrlInvalid] = useState<boolean>(false);
  const issuesShown: boolean = sectionShown === UpdateTemplateSection.ISSUES;
  const mainShown: boolean = sectionShown === UpdateTemplateSection.MAIN;
  const ppmShown: boolean = sectionShown === UpdateTemplateSection.PPM;
  const ppmEnabled: boolean = true;
  const confirmShown: boolean = ppmShown || (!ppmEnabled && issuesShown);
  const templateNameEmpty: boolean = !(selectedTemplateName?.length > 0);
  const templateNameDuplicate: boolean = templates
    .some((template: Template) => template.name.trim().toLowerCase()
      === selectedTemplateName.trim().toLowerCase()
      && template.id !== templateToUpdate?.id);
  const templateNameInvalid: boolean = templateNameEmpty || templateNameDuplicate;
  const linkRegex: RegExp = new RegExp(ADDITIONAL_FIELD_TYPES.LINK);

  const templateErrorMessage: () => string = () => {
    if (templateNameEmpty) return 'Template name cannot be empty';
    if (templateNameDuplicate) return TEMPLATE_NAME_DUPLICATE_ERROR;
    return '';
  };

  const restoreDefaults: () => void = () => {
    setInitialOption(templateToUpdate ? TemplateUpdateOption.EDIT : null);
    setSelectedTemplateType(templateToUpdate?.type ?? TemplateType.ASSET);
    setFocusedFieldIndex(-1);
    setPrimedTemplateId(templateToUpdate?.id);
    setSelectedTemplateId(templateToUpdate?.id);
    setSelectedTemplateName(templateToUpdate?.name ?? 'New template');
    setSelectedTemplateGroupId(defaultTemplateGroupID
      ?? templateGroups?.filter((group: TemplateGroup) => group
        .buttonPrototypes
        ?.map((item: Template) => item.id)?.includes(templateToUpdate?.id))[0]
        ?.ButtonPrototypeGroupId);
    setIssues(getIssueData(templateToUpdate?.issues) ?? customTemplateDefaultIssues());
    setSectionShown(UpdateTemplateSection.MAIN);
    setFields(templateToUpdate?.customfields ?? []);
    setPpmLink(templateToUpdate?.ppmUrl);
    setTemplateTitle(templateToUpdate?.title || DEFAULT_TEMPLATE_TITLE);
    setTemplateSubtitle(templateToUpdate?.subtitle ?? null);
    setTemplateSubtitleUrl(templateToUpdate?.subtitleUrl ?? null);
    setPpmLinkError(null);
    setTemplateGroupNotSelected(false);
    setUpdatedTemplate(null);
    setPhotoUploadEnabled(!templateToUpdate?.disablePhotoUpload);
    closeDropdowns();
  };

  const cancel: () => void = () => {
    closeDropdowns();
    if (ppmShown) setSectionShown(UpdateTemplateSection.ISSUES);
    else if (issuesShown) setSectionShown(UpdateTemplateSection.MAIN);
    else {
      setDefaultIssues();
      setTemplateGroupNotSelected(false);
      setSelectedTemplateType(templateToUpdate?.type ?? TemplateType.ASSET);
      if (initialOption === TemplateUpdateOption.DEFAULT
        || initialOption === TemplateUpdateOption.VARIANT) {
        if (selectedTemplateId) {
          setSelectedTemplateId(null);
          const timeout = setTimeout(() => {
            templateList?.current?.scrollToIndex({
              index: (initialOption === TemplateUpdateOption.DEFAULT ? PresetTemplates : templates)
                .findIndex((item) => item.id === primedTemplateId),
              viewPosition: 0.5,
              animated: false,
            });
            clearTimeout(timeout);
          }, 300);
        } else {
          setPrimedTemplateId(null);
          setSelectedTemplateId(null);
          restoreDefaults();
        }
      } else if (initialOption === TemplateUpdateOption.CUSTOM) restoreDefaults();
      else hide();
    }
  };

  useEffect(() => {
    const unsubscribe = navigation.addListener('blur', closeDropdowns);
    return unsubscribe;
  }, []);

  useEffect(() => setTemplateGroupNotSelected(false), [selectedTemplateGroupId]);

  useEffect(() => setSubtitleUrlInvalid(false), [templateSubtitleUrl]);

  const showError = (errorText?: string) => showAlert(
    'Error',
    errorText
    ?? `We are unable to ${templateToUpdate?.id ? 'edit' : 'add'} the template right now. Please try again later`,
  );

  const attemptUpdate = () => {
    setLoading(true);
    let issuesToUpdate: TemplateIssue[] = [...issues];
    if (initialOption !== TemplateUpdateOption.EDIT) {
      issuesToUpdate = issuesToUpdate?.map((issue: TemplateIssue) => ({
        Text: issue.Text,
        UserGroupId: issue.UserGroupId ?? [],
        Priority: issue.Priority,
        Enabled: issue.Enabled ?? true,
        Category: issue.Category,
      }));
    } else {
      issuesToUpdate = issuesToUpdate?.map((issue: TemplateIssue) => {
        const outdatedIssue: TemplateIssue = templateToUpdate?.issues
          ?.filter((originalIssue: TemplateIssue) => originalIssue?.Id === issue?.Id)[0];
        const updatedIssue: TemplateIssue = {
          Id: issue.Id,
          Text: issue.Text,
          UserGroupId: issue.UserGroupId ?? [],
          Enabled: issue.Enabled ?? true,
          Priority: issue.Priority,
          Category: issue.Category,
        };
        if (outdatedIssue?.UserGroupId?.length > 0) {
          updatedIssue.PrevUserGroupId = [];
          outdatedIssue.UserGroupId.forEach((groupId: string) => {
            if (!updatedIssue.UserGroupId.includes(groupId)) {
              updatedIssue.PrevUserGroupId.push(groupId);
            }
          });
        }
        return updatedIssue;
      });
    }
    const templateGroupName: string = templateGroups?.filter((group: TemplateGroup) => group
      .ButtonPrototypeGroupId === selectedTemplateGroupId)[0]?.Name ?? '';
    let ppmLinkWithoutId = ppmLink;
    if (ppmLink && !ppmLink?.trim().endsWith('=')) {
      ppmLinkWithoutId = `${ppmLink?.slice(0, ppmLink.lastIndexOf('='))}=`;
    }
    updateTemplate({
      Name: selectedTemplateName.trim(),
      GroupId: selectedTemplateGroupId,
      GroupName: templateGroupName.trim(),
      Fields: fields,
      Issues: issuesToUpdate,
      ButtonPrototypeId: templateToUpdate?.id,
      Type: selectedTemplateType ?? TemplateType.ASSET,
      PpmUrl: ppmLinkWithoutId?.trim(),
      Title: templateTitle?.trim(),
      Subtitle: templateSubtitle?.trim(),
      SubtitleUrl: templateSubtitleUrl?.trim(),
      disablePhotoUpload: !photoUploadEnabled,
    }).then((response) => {
      if (response && !response.isError) {
        const responseTemplate = response.ButtonPrototype
          ?? JSON.parse(response.msg.body).ButtonPrototype;
        const savedTemplate: Template = {
          name: responseTemplate.Name,
          id: responseTemplate.ButtonPrototypeId,
          issues: responseTemplate.Issues,
          customfields: responseTemplate.CustomFields,
          type: responseTemplate.Type,
          ppmUrl: responseTemplate.PpmUrl,
          title: responseTemplate?.Title,
          subtitle: responseTemplate?.Subtitle,
          subtitleUrl: responseTemplate?.SubtitleUrl,
          disablePhotoUpload: responseTemplate?.DisablePhotoUpload ?? !photoUploadEnabled,
        };
        setUpdatedTemplate(savedTemplate);
        if (Platform.OS === 'ios') {
          showAlert(
            `The template was successfully ${templateToUpdate?.id ? 'edited' : 'added'}`,
            <Text>
              {'Template '}
              <Text style={Style.alertBoldText}>
                {selectedTemplateName?.trim()}
              </Text>
              {` successfully ${templateToUpdate?.id ? 'edited' : 'added'}`}
              {!templateToUpdate?.id && templateGroupName?.length
                && <Text>
                  {' to template group '}
                  <Text style={Style.alertBoldText}>{templateGroupName}</Text>
                </Text>}
            </Text>,
            false,
          );
        } else {
          update(savedTemplate, !templateToUpdate?.id, selectedTemplateGroupId);
          hide();
          showParentAlert(
            `Template ${templateToUpdate?.id ? 'edited' : 'added'} successfully`,
            <Text>
              {'Template '}
              <Text style={Style.alertBoldText}>
                {selectedTemplateName?.trim()}
              </Text>
              {` successfully ${templateToUpdate?.id ? 'edited' : 'added'}`}
              {!templateToUpdate?.id && templateGroupName?.length
                && <Text>
                  {' to template group '}
                  <Text style={Style.alertBoldText}>{templateGroupName}</Text>
                </Text>}
            </Text>,
            false,
          );
        }
      } else showError(response?.errorText);
      setLoading(false);
    }).catch((error) => showError(error?.message));
  };

  const onAlertDismiss: () => void = () => {
    setAlertDismissed(true);
    if (Platform.OS === 'ios' && updatedTemplate) {
      hide();
      update(updatedTemplate, !templateToUpdate?.id, selectedTemplateGroupId);
    }
  };

  const closeDropdowns: () => void = () => {
    setTemplateGroupDropdownShown(false);
    setTemplateTypeDropdownShown(false);
    setFieldTypesShownIndex(null);
    setPriorityDropdownShownIndex(null);
    setIssueTagDropdownShownIndex(null);
    setTagDropdownShownIndex(null);
    setCategoryDropdownShownIndex(null);
  };

  const validate = (): boolean => {
    const errorMessages: string[] = [];
    if (!selectedTemplateName?.trim().length) {
      setFocusedFieldIndex(0);
      errorMessages.push('- Template name cannot be empty');
      setSectionShown(UpdateTemplateSection.MAIN);
    }
    if (!nameRegex.exp.test(selectedTemplateName?.trim())) {
      if (!errorMessages.length) {
        setFocusedFieldIndex(0);
        setSectionShown(UpdateTemplateSection.MAIN);
      }
      errorMessages.push(`- Template name '${selectedTemplateName?.trim()}' invalid`);
      errorMessages.push(`- Template name${nameRegex.message}`);
    }
    if (templateNameDuplicate) {
      if (!errorMessages.length) {
        setFocusedFieldIndex(0);
        setSectionShown(UpdateTemplateSection.MAIN);
      }
      errorMessages.push(`- ${TEMPLATE_NAME_DUPLICATE_ERROR}`);
    }
    if (!selectedTemplateGroupId) {
      setTemplateGroupNotSelected(true);
      if (!errorMessages.length) {
        setFocusedFieldIndex(1);
        setSectionShown(UpdateTemplateSection.MAIN);
      }
      errorMessages.push('- A template group must be chosen to add the template to');
    }
    if (templateSubtitleUrl?.trim()
      && !linkRegex.test(templateSubtitleUrl.trim())) {
      setSectionShown(UpdateTemplateSection.MAIN);
      errorMessages.push(`- Template subtitle URL '${templateSubtitleUrl?.trim()}' invalid`);
      setSubtitleUrlInvalid(true);
    }
    fields?.forEach((field: TemplateField, index: number) => {
      if (!(field.Name?.trim().length > 0)) {
        if (!errorMessages.length) {
          setFocusedFieldIndex(index + 2);
          setSectionShown(UpdateTemplateSection.MAIN);
        }
        errorMessages.push('- Additional field names cannot be empty');
        return;
      }
      if (!nameRegex.exp.test(field.Name?.trim())) {
        if (!errorMessages.length) {
          setFocusedFieldIndex(index + 2);
          setSectionShown(UpdateTemplateSection.MAIN);
        }
        errorMessages.push(`- Additional field name '${field.Name?.trim()}' invalid`);
        errorMessages.push(`- Additional field names${nameRegex.message}`);
      }
      fields.slice(0, index)?.forEach((previousField: TemplateField) => {
        if (previousField.Name.trim().toLowerCase() === field.Name.trim().toLowerCase()) {
          if (!errorMessages.length) {
            setFocusedFieldIndex(index + 2);
            setSectionShown(UpdateTemplateSection.MAIN);
          }
          errorMessages
            .push(`- Field name '${field.Name}' is a duplicate. Field names must be unique within the template`);
        }
      });
    });
    if (issues?.length > 1) {
      let enabledIssuesExist: boolean = false;
      issues.forEach((issue: TemplateIssue, index: number) => {
        if (issue.Enabled) {
          enabledIssuesExist = true;
          if (!issue.Text?.trim().length) {
            if (!errorMessages.length) setSectionShown(UpdateTemplateSection.ISSUES);
            errorMessages.push('- Issue title cannot be empty');
            return;
          }
          if (!nameRegex.exp.test(issue.Text?.trim())) {
            if (!errorMessages.length) setSectionShown(UpdateTemplateSection.ISSUES);
            errorMessages.push(`- Issue name '${issue.Text?.trim()}' invalid`);
            errorMessages.push(`- Issue titles${nameRegex.message}`);
          } else {
            issues.slice(0, index)?.forEach((previousIssue: TemplateIssue) => {
              if (previousIssue.Text.trim().toLowerCase() === issue.Text.trim().toLowerCase()) {
                if (!errorMessages.length) setSectionShown(UpdateTemplateSection.ISSUES);
                errorMessages
                  .push(`- Issue name '${issue.Text}' is a duplicate. Issue names must be unique within the template`);
              }
            });
          }
          const issueUserGroups: string[] = issue.UserGroupId?.filter((id: string) => userGroups
            .map((userGroup: UserGroup) => userGroup.UserGroupId).includes(id));
          if (!issueUserGroups?.length) {
            if (!errorMessages.length) setSectionShown(UpdateTemplateSection.ISSUES);
            errorMessages.push(`- Issue '${issue.Text?.trim()}' is not assigned to any user group`);
          }
          if (![0, 1, 2, 3, 4].includes(issue.Priority)) {
            if (!errorMessages.length) setSectionShown(UpdateTemplateSection.ISSUES);
            errorMessages
              .push(`- Issue '${issue.Text?.trim()}' does not have a priority level specified`);
          }
        }
      });
      if (!enabledIssuesExist) {
        if (!errorMessages.length) setSectionShown(UpdateTemplateSection.ISSUES);
        errorMessages.push('- At least one issue must be enabled');
      }
    } else {
      if (!errorMessages.length) setSectionShown(UpdateTemplateSection.ISSUES);
      errorMessages.push('- A template should have at least one issue');
    }
    if (ppmEnabled && ppmLink?.trim().length > 0 && !linkRegex.test(ppmLink?.trim())) {
      if (!errorMessages.length) setSectionShown(UpdateTemplateSection.PPM);
      setPpmLinkError('Value is not a valid hyperlink');
      errorMessages.push('- PPM link provided is not a valid hyperlink');
    } else setPpmLinkError(null);
    if (errorMessages.length) showAlert('Error', Array.from(new Set(errorMessages)).join('\n'));
    return !errorMessages.length;
  };

  const continuePress: () => void = () => {
    closeDropdowns();
    if (loading) return;
    if ((initialOption === TemplateUpdateOption.DEFAULT
      || initialOption === TemplateUpdateOption.VARIANT)
      && !selectedTemplateId) setSelectedTemplateId(primedTemplateId);
    else if (sectionShown === UpdateTemplateSection.MAIN) {
      setSectionShown(UpdateTemplateSection.ISSUES);
    } else if (sectionShown === UpdateTemplateSection.ISSUES && ppmEnabled) {
      setSectionShown(UpdateTemplateSection.PPM);
    } else if (validate()) attemptUpdate();
  };

  const getIssueData = (issueList: TemplateIssue[]) => {
    if (!issueList) return null;
    const newIssueList: TemplateIssue[] = issueList
      .filter((issue) => issue.Text?.length > 0 && issue.Text.toLowerCase() !== 'other')
      .map((issue: any) => ({
        UserGroupId: [...issue.UserGroupId ?? []],
        Text: issue.Text,
        Priority: issue.Priority,
        Id: issue.Id,
        Enabled: issue.Enabled ?? true,
        Category: issue.Category,
      }));
    const otherIssue: TemplateIssue = issueList
      .filter((issue) => issue.Text?.length > 0 && issue.Text.toLowerCase() === 'other')[0];
    if (otherIssue) {
      newIssueList.push({
        UserGroupId: [...otherIssue.UserGroupId ?? []],
        Text: otherIssue.Text,
        Priority: otherIssue.Priority,
        Id: otherIssue.Id,
        Enabled: otherIssue.Enabled ?? true,
      });
    }
    return newIssueList;
  };

  const setDefaultIssues: () => void = () => {
    if (templateToUpdate?.issues) setIssues(getIssueData(templateToUpdate.issues));
    else {
      const selectedTemplate: Template = (initialOption === TemplateUpdateOption.DEFAULT
        ? PresetTemplates : templates)
        ?.filter((template: Template) => template.id === primedTemplateId)[0];
      setIssues(getIssueData(selectedTemplate?.issues) ?? customTemplateDefaultIssues());
    }
  };

  useEffect(() => {
    if (alertShown) setAlertDismissed(false);
  }, [alertShown]);

  const focusField: () => void = () => {
    if (issuesShown) {
      setTemplateGroupDropdownShown(false);
      setTemplateTypeDropdownShown(false);
    }
    let count: number = 0;
    let fieldIndex: number = focusedFieldIndex;
    if (count < 5 && fieldIndex >= 0 && sectionShown !== UpdateTemplateSection.ISSUES && !alertShown
      && ((focusedFieldIndex === 0 && nameField?.current) || focusedFieldIndex > 1)) {
      count += 1;
      if (focusedFieldIndex === 0) nameField?.current?.focus();
      else if (focusedFieldIndex > 1) fieldRefs[focusedFieldIndex - 2]?.focus();
      fieldIndex = -1;
      if (!(focusedFieldIndex > 1)) setFocusedFieldIndex(-1);
    }
  };

  useEffect(focusField, [
    sectionShown, focusedFieldIndex, nameField?.current, alertDismissed,
    fieldRefs[focusedFieldIndex],
  ]);

  useEffect(closeDropdowns, [sectionShown]);

  useEffect(() => setPpmLinkError(null), [ppmLink]);

  useEffect(() => {
    if (fieldTypesShownIndex !== null) {
      setTemplateGroupDropdownShown(false);
      setTemplateTypeDropdownShown(false);
    }
  }, [fieldTypesShownIndex]);

  useEffect(() => {
    const selectedTemplate: Template = templateToUpdate
      ?? (initialOption === TemplateUpdateOption.DEFAULT ? PresetTemplates : templates)
        ?.filter((template: Template) => template.id === selectedTemplateId)[0];
    setSelectedTemplateName(selectedTemplate?.name ?? 'New template');
    setTemplateTitle(selectedTemplate?.title ?? DEFAULT_TEMPLATE_TITLE);
    setTemplateSubtitle(selectedTemplate?.subtitle ?? '');
    setFields([...(selectedTemplate?.customfields ?? [])
      ?.map((field: TemplateField) => ({ ...field })) ?? []]);
    setDefaultIssues();
    setSelectedTemplateGroupId(defaultTemplateGroupID
      ?? templateGroups?.filter((group: TemplateGroup) => group.buttonPrototypes
        ?.map((item: Template) => item.id)?.includes(templateToUpdate?.id))[0]
        ?.ButtonPrototypeGroupId);
  }, [selectedTemplateId]);

  const loadData = () => {
    getUserGroups().then((response: { UserGroups?: UserGroup[] }) => {
      setUserGroups(response?.UserGroups?.sort((a: UserGroup, b: UserGroup) => a.Name?.toLowerCase()
        .localeCompare(b.Name?.toLowerCase())) ?? []);
    });
  };

  useEffect(() => {
    if (isVisible) {
      setModalVisible(true);
      loadData();
      setVisibilityToggleEnabled(false);
      const timeout = setTimeout(() => {
        setVisibilityToggleEnabled(true);
        clearTimeout(timeout);
      }, 500);
    } else {
      setVisibilityToggleEnabled(true);
      setModalVisible(false);
    }
  }, [isVisible]);

  const closeModal: () => void = () => (visibilityToggleEnabled ? hide() : null);

  const showAlert = (
    title: string,
    message: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setLoading(false);
    setAlertShown(true);
  };

  const scrollToEnd: () => void = () => {
    const timeout = setTimeout(() => {
      scrollView.current?.scrollToEnd();
      clearTimeout(timeout);
    }, 200);
  };

  const editName: () => void = () => {
    logEvent('AddTemplateModal', 'edit_icon');
    setSectionShown(UpdateTemplateSection.MAIN);
    setFocusedFieldIndex(0);
  };

  const content = () => <View style={[
    Style.modalContent, styles.container,
    { paddingBottom: styles.container.paddingBottom + insets.bottom },
    !initialOption && styles.initialContainer,
  ]}>
    <Text style={[styles.title, Platform.OS === 'ios' && { marginTop: insets.top - 12 }]}>
      {!!initialOption
        && (((initialOption === TemplateUpdateOption.DEFAULT
          || initialOption === TemplateUpdateOption.VARIANT)
          && !selectedTemplateId) ? 'Choose preset' : <>
          {initialOption === TemplateUpdateOption.EDIT ? 'Edit ' : 'Add '}
          {mainShown && 'template'}
          {issuesShown && 'issues'}
          {ppmShown && 'PPM information'}
        </>)}
      {!initialOption && 'Choose template or create new one'}
    </Text>
    {initialOption && ((selectedTemplateId || initialOption === TemplateUpdateOption.CUSTOM) ? <>
      <TabList {...{ sectionShown, setSectionShown, ppmEnabled }} />
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={styles.scrollViewContent}
          nestedScrollEnabled
          ref={scrollView}
        >
          {mainShown && <>
            <MainSection
              {...{
                templateGroupDropdownShown,
                setTemplateGroupDropdownShown,
                templateTypeDropdownShown,
                setTemplateTypeDropdownShown,
                templateGroupNotSelected,
                templateGroups,
                selectedTemplateGroupId,
                setFieldTypesShownIndex,
                selectedTemplateName,
                setSelectedTemplateName,
                closeDropdowns,
                nameField,
                setSelectedTemplateGroupId,
                selectedTemplateType,
                setSelectedTemplateType,
                happyEnabled,
                templateTitle,
                setTemplateTitle,
                templateSubtitle,
                setTemplateSubtitle,
                templateSubtitleUrl,
                setTemplateSubtitleUrl,
                templates,
                subtitleUrlInvalid,
                templateNameInvalid,
                templateErrorMessage,
              }}
              currentTemplate={templateToUpdate}
            />
            <FieldsList
              {...{
                fields,
                setFields,
                fieldRefs,
                fieldTypesShownIndex,
                setFieldTypesShownIndex,
                scrollToEnd,
              }}
            />
          </>}
          {issuesShown && <IssueList
            {...{
              setIssues,
              issues,
              userGroups,
              tagDropdownShownIndex,
              priorityDropdownShownIndex,
              issueTagDropdownShownIndex,
              setTagDropdownShownIndex,
              setPriorityDropdownShownIndex,
              setIssueTagDropdownShownIndex,
              categoryDropdownShownIndex,
              setCategoryDropdownShownIndex,
              scrollToEnd,
              slaNames,
              selectedTemplateName,
              editName,
              photoUploadEnabled,
              setPhotoUploadEnabled,
            }}
          />}
          {ppmShown && <CustomTextField
            title='PPM link'
            placeholder='Enter PPM link'
            value={ppmLink}
            updateValue={setPpmLink}
            invalid={!!ppmLinkError}
            errorText={ppmLinkError}
          />}
        </ScrollView>
      </KeyboardAvoidingView>
    </> : <TemplateList
      {...{ primedTemplateId, setPrimedTemplateId, templateList }}
      data={initialOption === TemplateUpdateOption.DEFAULT ? PresetTemplates : templates}
      testID={initialOption === TemplateUpdateOption.DEFAULT ? 'default-templates-list' : null}
    />)}
    {initialOption ? <View style={styles.buttonContainer}>
      <Button
        onPress={cancel}
        title={(templateToUpdate && mainShown) ? 'Cancel' : 'Back'}
      />
      <Button
        disabled={loading
          || ((initialOption === TemplateUpdateOption.DEFAULT
            || initialOption === TemplateUpdateOption.VARIANT)
            && !primedTemplateId)}
        onPress={continuePress}
        affirmative
        title={confirmShown ? 'Confirm' : 'Continue'}
        testID={ppmShown ? 'confirm-button' : 'continue-btn'}
      />
    </View> : <CreationOptionList {...{ setInitialOption }} />}
    <ActivityIndicator
      size='large'
      animating={loading}
      style={Style.activityIndicator}
    />
    <PressableStyled
      onPress={hide}
      style={[
        Style.closeButton, Platform.OS === 'ios' && { top: Style.closeButton.top + insets.top },
      ]}
      testID='close-create-template-modal'
    >
      <CloseModalIcon />
    </PressableStyled>
  </View>;

  return <Modal
    isVisible={modalVisible}
    onSwipeComplete={closeModal}
    onBackButtonPress={closeModal}
    onBackdropPress={closeModal}
    style={styles.modal}
    onModalHide={restoreDefaults}
    onModalWillShow={restoreDefaults}
  >
    {isMobile ? content() : <Pressable
      onPress={closeDropdowns}
      children={content()}
      style={styles.wrapper}
    />}
    <AlertModal
      onModalHide={onAlertDismiss}
      modalVisible={alertShown}
      setModalVisible={setAlertShown}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      action={alertAction}
    />
  </Modal>;
};

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'center',
    alignItems: 'center',
    margin: 0,
  },
  wrapper: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    paddingBottom: isMobile ? 16 : 40,
    margin: isMobile ? 16 : 20,
    paddingTop: 60,
    borderRadius: isMobile ? 0 : 10,
    backgroundColor: '#FFF',
    width: isMobile ? '100%' : 530,
    minHeight: isMobile ? '100%' : 467,
    maxHeight: isMobile ? '100%' : '75%',
    cursor: 'auto',
  },
  initialContainer: {
    justifyContent: 'center',
  },
  scrollViewContent: {
    paddingHorizontal: isMobile ? 16 : 40,
    paddingVertical: 12,
  },
  title: {
    fontSize: 28,
    lineHeight: 35,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    textAlign: 'center',
    marginBottom: 32,
    width: isMobile ? '75%' : 356,
    alignSelf: 'center',
  },
  buttonContainer: {
    alignSelf: 'center',
    flexDirection: 'row',
    marginTop: isMobile ? 27 : 40,
    paddingHorizontal: isMobile ? 16 : 40,
  },
});

export default AddTemplateModal;
